/**
 * useProfileSocketSync: keeps the signed-in person's profile, store and
 * verifications current on every page. Mounted in the header beside the
 * other socket listeners.
 *
 * The server pushes USER_SOCKET_EVENTS.PROFILE_CHANGED to the person's own
 * room when something away from their pages changes them (backend
 * services/userService.js, profileChanged): the email link, opened in a tab
 * of its own; the ID check's result from Stripe; a phone code; an admin's
 * decision on them or their store, like turning its API on. The pages keep
 * what they read until something marks it stale, so this marks the profile,
 * the store, the verifications and the cart (which says why checkout waits
 * on the ID) stale, and every page showing them reads
 * again with no reload (Tyler, 2026-10-16: "i had to hard refresh the page
 * to get the api table there"). The push carries nothing to read.
 *
 * On Socket.IO's own "connect", which fires on every reconnect, the same is
 * marked stale: a push missed while the socket was down is not replayed.
 */
import { useEffect } from 'react'
import { USER_SOCKET_EVENTS } from '@gearghost/api-schemas/user'
import { PROFILE_TAGS, userApi } from '@/redux/api/userApi'
import { cartApi } from '@/redux/api/cartApi'
import { useAppDispatch } from '@/redux/hooks'
import { SOCKET_CONNECT, useSocket } from '@/redux/socketContext'

export const useProfileSocketSync = () => {
  const { socket } = useSocket()
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (!socket) return
    const readAgain = () => {
      dispatch(userApi.util.invalidateTags([...PROFILE_TAGS]))
      // The cart says why checkout waits on the ID (idCheckoutMessage):
      // Stripe's verdict lets the renter through with no reload (Tyler,
      // 2026-10-23).
      dispatch(cartApi.util.invalidateTags(['Cart']))
    }
    socket.on(USER_SOCKET_EVENTS.PROFILE_CHANGED, readAgain)
    socket.on(SOCKET_CONNECT, readAgain)
    return () => {
      socket.off(USER_SOCKET_EVENTS.PROFILE_CHANGED, readAgain)
      socket.off(SOCKET_CONNECT, readAgain)
    }
  }, [socket, dispatch])
}
